import React, { useCallback, useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import { Menu, MenuItem, MenuButton, useMenuProps } from '../../../../components/DropdownSet';
import AppIcon from '../../../../components/AppIcon';
import Skeleton from '../../../../components/Skeleton';

const emptyArray = [];

const renderOptionLabel = (app, { size = 24, className } = {}) => (
  <div className={cx('company-apps-dropdown-label', className)}>
    <AppIcon
      alt={app.nickname}
      url={app.icon_url}
      store={app.store}
      size={size}
      hideHtmlTitle
      withBorder
      containerClasses="company-apps-dropdown-label__icon"
    />
    <span className="company-apps-dropdown-label__name">{app.nickname}</span>
  </div>
);
const makeAppsOptions = (apps = []) =>
  apps.map((app) => ({
    value: {
      unified_ref_no: app.unified_ref_no,
      store: app.store,
    },
    label: renderOptionLabel(app),
    buttonLabel: renderOptionLabel(app, {
      size: 20,
      className: 'company-apps-dropdown-label--button',
    }),
    sector: app.sector,
  }));

const AppsDropdown = ({
  selected,
  options = emptyArray,
  disabled = false,
  pending = false,
  onChange,
}) => {
  const menuProps = useMenuProps({
    menuStyle: { width: 251 },
    className: 'company-apps-dropdown-menu',
  });

  const appsOptions = useMemo(() => makeAppsOptions(options), [options]);

  // Selected option
  const selectedOption = useMemo(
    () => appsOptions?.find((option) => option.value?.unified_ref_no === selected) || {},
    [appsOptions, selected]
  );

  const handleChange = useCallback(
    ({ value }) => {
      onChange(value.unified_ref_no);
    },
    [onChange]
  );

  if (pending) return <Skeleton width={120} height={32} />;

  const renderMenuOptions = () => {
    const renderMenuOption = (option, index) => {
      const { label, value, disabled: isDisabled = false } = option;
      return (
        <MenuItem
          key={value.unified_ref_no || index}
          value={value}
          disabled={isDisabled}
          className={cx('menu-item--14', {
            active: selectedOption.value?.unified_ref_no === value.unified_ref_no,
          })}
          onClick={handleChange}
        >
          {label}
        </MenuItem>
      );
    };

    return appsOptions.map(renderMenuOption);
  };

  return (
    <Menu
      menuButton={
        <MenuButton disabled={disabled}>
          {selectedOption.buttonLabel || selectedOption.label || 'Select App'}
        </MenuButton>
      }
      setDownOverflow
      {...menuProps}
    >
      <div>{renderMenuOptions()}</div>
    </Menu>
  );
};

AppsDropdown.propTypes = {
  selected: T.oneOfType([T.number, T.string]),
  options: T.array,
  disabled: T.bool,
  pending: T.bool,
  onChange: T.func,
};

export default AppsDropdown;
